import { describeSummaryValue } from '@fit-editor/fit-core'
import type { Provenance, SummaryValue } from '@fit-editor/fit-core'

import { Badge } from '@/components/ui/badge'
import { Tooltip } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

/**
 * Visual treatment per provenance tag. Deliberately combines a
 * color-adjacent utility (drawn only from the existing light/dark CSS
 * variables, never a hardcoded color) with a non-color cue (dashed
 * border for "estimated", dotted underline for "edited", italics for
 * "unavailable") so the distinction
 * never relies on color alone.
 */
const PROVENANCE_LABEL: Record<Provenance, string> = {
  original: 'Original',
  recalculated: 'Recalculated',
  estimated: 'Estimated',
  edited: 'Edited',
  unavailable: 'Unavailable',
}

const PROVENANCE_CLASSNAME: Record<Provenance, string> = {
  original: 'border-border bg-background text-foreground',
  recalculated: 'border-primary/40 bg-primary/10 text-primary',
  estimated: 'border-dashed border-border bg-accent text-accent-foreground',
  edited: 'border-warning/60 bg-warning/10 text-warning-text underline decoration-dotted',
  unavailable: 'border-border bg-muted text-muted-foreground italic',
}

/**
 * One provenance-tagged value inline (for the collapsed dashboard's
 * one-line summary): label, value and the same provenance badge as
 * `ProvenanceStat`, just smaller, so the original/recalculated/estimated/
 * unavailable distinction stays visible when the cards are collapsed.
 */
export function InlineProvenanceValue<T>({
  label,
  value,
  format,
}: Pick<ProvenanceStatProps<T>, 'label' | 'value' | 'format'>) {
  const description = describeSummaryValue(value)
  const display = value.value == null ? 'Not available' : format(value.value)
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-foreground font-medium">{display}</span>
      <Tooltip content={description.explanation}>
        <Badge className={cn('px-1.5 py-0 text-[11px]', PROVENANCE_CLASSNAME[value.provenance])}>
          {PROVENANCE_LABEL[value.provenance]}
        </Badge>
      </Tooltip>
    </span>
  )
}

export interface ProvenanceStatProps<T> {
  label: string
  value: SummaryValue<T>
  format: (value: T) => string
  /** Optional one-line note under the value (e.g. where an estimate came from). */
  hint?: string
  /**
   * Render as a `<dt>`/`<dd>` pair wrapper, for use inside a `<dl>` stat
   * grid alongside `PlainStat`s (a bare `<div>` of spans isn't valid `<dl>` content).
   */
  asDefinition?: boolean
}

/**
 * One dashboard stat whose value was produced by `fit-core`'s calculation
 * engine. Always renders the provenance explicitly (per the master spec's
 * requirement that original/recalculated/estimated/unavailable values be
 * visibly distinguished, not just tracked internally) and never collapses
 * "unavailable" into a blank or a zero.
 */
export function ProvenanceStat<T>({
  label,
  value,
  format,
  hint,
  asDefinition = false,
}: ProvenanceStatProps<T>) {
  const description = describeSummaryValue(value)
  const display = value.value == null ? 'Not available' : format(value.value)
  const LabelTag = asDefinition ? 'dt' : 'span'
  const BodyTag = asDefinition ? 'dd' : 'div'

  return (
    <div className="border-border bg-card flex min-w-0 flex-col gap-0.5 rounded-md border px-2.5 py-1.5">
      <LabelTag className="text-muted-foreground truncate text-xs">{label}</LabelTag>
      <BodyTag className="flex min-w-0 flex-col gap-0.5">
        <span className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5">
          <span className="text-card-foreground text-sm font-semibold">{display}</span>
          <Tooltip content={description.explanation}>
            <Badge
              className={cn(
                'w-fit px-1.5 py-0 text-[10px]',
                PROVENANCE_CLASSNAME[value.provenance],
              )}
            >
              {PROVENANCE_LABEL[value.provenance]}
            </Badge>
          </Tooltip>
        </span>
        {hint != null && <span className="text-muted-foreground text-[11px]">{hint}</span>}
      </BodyTag>
    </div>
  )
}
